import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DeepPartialSkipArrayKey,
  FieldValues,
  UseFormTrigger,
  useFormState,
  useWatch
} from "react-hook-form";

import type { SxProps } from "@mui/material/styles";

import { datadogLogs } from "@datadog/browser-logs";
import { isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";

import DataSaveState, { DataSaveStateStatus } from "components/data-save-state";
import useDebounce from "hooks/use-debounce.hook";

// Global save coordination across all hook instances
type PendingItem = { data: any; timestamp: number } | null;
type GroupState = {
  inFlight: boolean;
  pending: PendingItem;
  refs: number;
  deleteWhenIdle?: boolean;
};
const saveGroups = new Map<string, GroupState>();
const getGroupState = (key: string): GroupState => {
  if (!saveGroups.has(key)) {
    saveGroups.set(key, { inFlight: false, pending: null, refs: 0 });
  }
  return saveGroups.get(key)!;
};

export default function useFormAutoSave({
  control,
  updateData,
  formatter,
  allowSavingInvalidForm = false,
  delay,
  reset,
  sx,
  afterUpdate,
  skipUpdateComparator,
  trigger,
  dataDogLabel,
  groupKey = "autosave-global"
}: {
  control: any;
  updateData: any;
  formatter?: Function;
  allowSavingInvalidForm?: boolean;
  delay?: number;
  reset?: any;
  sx?: SxProps;
  afterUpdate?: () => void;
  skipUpdateComparator?: (newData: any) => boolean;
  trigger?: UseFormTrigger<any>;
  dataDogLabel?: string;
  groupKey?: string;
}) {
  const [autosaveIsTriggered, setAutosaveIsTriggered] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastChanged, setLastChanged] = useState(Date.now());
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [dataState, setDataState] = useState<DataSaveStateStatus>("saved");
  const [hasError, setHasError] = useState(false);
  // Prevent overlapping saves within the same render tick and skip duplicate saves
  const isSavingRef = useRef(false);
  const lastSavedTimestampRef = useRef<number>(0);
  const mountedRef = useRef(true);

  const formState = useFormState({ control });
  const formData = useWatch({ control });
  const { value: debouncedFormData, isDebouncing } = useDebounce(
    formData,
    delay
  );

  const dataSaveStateContent = useMemo(
    () => <DataSaveState state={dataState} sx={sx} />,
    [dataState, sx]
  );

  // Track mount state and manage group reference counting for cleanup
  useEffect(() => {
    mountedRef.current = true;
    const group = getGroupState(groupKey);
    group.refs = (group.refs ?? 0) + 1;
    return () => {
      mountedRef.current = false;
      const currentGroup = getGroupState(groupKey);
      currentGroup.refs = Math.max(0, (currentGroup.refs ?? 1) - 1);
      if (currentGroup.refs === 0) {
        if (currentGroup.inFlight) {
          // Mark for deletion once the current in-flight operation completes
          currentGroup.deleteWhenIdle = true;
          currentGroup.pending = null;
        } else {
          saveGroups.delete(groupKey);
        }
      }
    };
  }, [groupKey]);

  useEffect(() => {
    if (hasError) {
      return;
    }

    const shouldSetDirty =
      autosaveIsTriggered ||
      (formState.isDirty &&
        !skipUpdateComparator?.(formData) &&
        lastChanged > lastUpdated &&
        dataState !== "dirty");

    if (shouldSetDirty) {
      setDataState("dirty");
      return;
    }
    if (!formState.isDirty && dataState === "dirty") {
      setLastUpdated(Date.now());
      setDataState("saved");
    }
  }, [
    formState.isDirty,
    formData,
    skipUpdateComparator,
    lastUpdated,
    lastChanged,
    dataState,
    autosaveIsTriggered,
    hasError
  ]);

  const handleAutoSave = useCallback(
    async (data: DeepPartialSkipArrayKey<FieldValues>, timestamp: number) => {
      const trn_id = uuidv4();
      const url = window.location.href;
      try {
        // Skip if a save is already in progress or we already saved this exact change
        if (
          isSavingRef.current ||
          lastSavedTimestampRef.current === timestamp
        ) {
          return;
        }

        const group = getGroupState(groupKey);
        // If there are no active listeners for this group, skip saving
        if (group.refs === 0) {
          return;
        }
        if (group.inFlight) {
          // coalesce concurrent saves in the same group – keep only the latest
          group.pending = { data, timestamp };
          return;
        }

        group.inFlight = true;
        isSavingRef.current = true;
        if (mountedRef.current) {
          setIsUpdating(true);
        }
        const requestData = formatter ? formatter(data) : data;
        const start = performance.now();
        if (dataDogLabel) {
          datadogLogs.logger.log(`useFormAutoSave ${dataDogLabel} request`, {
            state: "pending",
            type: "start",
            url,
            trn_id,
            started_at: new Date().getTime(),
            request: requestData
          });
        }

        const response = await updateData(requestData);
        const end = performance.now();
        if (dataDogLabel) {
          const isError = Boolean(response && "error" in response);
          datadogLogs.logger.log(`useFormAutoSave ${dataDogLabel} request`, {
            state: isError ? "failure" : "success",
            type: "end",
            url,
            trn_id,
            finished_at: new Date().getTime(),
            execution_ms: end - start,
            request: requestData,
            response
          });
        }
        if (mountedRef.current) {
          setDataState("saved");
          setHasError(false);
          setLastUpdated(timestamp);
        }
        lastSavedTimestampRef.current = timestamp;
        afterUpdate?.();
      } catch (error: any) {
        if (dataDogLabel) {
          datadogLogs.logger.error(`useFormAutoSave ${dataDogLabel} request`, {
            state: "failure",
            type: "end",
            url,
            trn_id,
            finished_at: new Date().getTime(),
            request: data,
            error: error?.message ?? error,
            stack: error?.stack
          });
        }
        if (mountedRef.current) {
          setDataState("error");
          setHasError(true);
          setLastUpdated(timestamp);
        }
        console.error("Auto-save failed:", error);
      } finally {
        const group = getGroupState(groupKey);
        const next = group.pending;
        group.pending = null;
        group.inFlight = false;
        isSavingRef.current = false;
        if (mountedRef.current) {
          setIsUpdating(false);
        }
        // If no more listeners for this group and nothing pending, prune the group entry
        if (
          group.deleteWhenIdle &&
          group.refs === 0 &&
          !group.pending &&
          !group.inFlight
        ) {
          saveGroups.delete(groupKey);
        }
        // If there was a pending save while the current one was in-flight, run it now
        if (next) {
          // schedule on the macrotask queue to avoid deep recursion
          if (group.refs > 0) {
            setTimeout(() => {
              handleAutoSave(next.data, next.timestamp);
            }, 0);
          }
        }
      }
    },
    [groupKey, formatter, dataDogLabel, updateData, afterUpdate]
  );

  useEffect(() => {
    if (!isEqual(debouncedFormData, formData)) {
      setLastChanged(Date.now());
      setHasError(false);
    }
  }, [debouncedFormData, formData]);

  useEffect(() => {
    const isFormValid = formState.isValid || allowSavingInvalidForm;
    const isLatestData = isEqual(debouncedFormData, formData);
    const shouldSave =
      (autosaveIsTriggered && !formState.isValidating) ||
      (!formState.isValidating &&
        formState.isDirty &&
        isLatestData &&
        !isUpdating &&
        !isSavingRef.current &&
        lastChanged > lastUpdated);

    if (!shouldSave) return;

    if (!isFormValid) {
      if (dataState !== "error") {
        trigger?.();
      }

      setDataState("error");
      setHasError(true);
      return;
    }

    if (!autosaveIsTriggered && skipUpdateComparator?.(formData)) {
      setDataState("saved");
      setHasError(false);
      setLastUpdated(lastChanged);
      return;
    }

    const dataToSave = autosaveIsTriggered ? formData : debouncedFormData;
    handleAutoSave(dataToSave, lastChanged);
    setAutosaveIsTriggered(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formState.isDirty,
    formState.isValid,
    formState.isValidating,
    debouncedFormData,
    formData,
    isUpdating,
    allowSavingInvalidForm,
    skipUpdateComparator,
    lastUpdated,
    lastChanged,
    autosaveIsTriggered,
    handleAutoSave,
    trigger
  ]);

  useEffect(() => {
    const isLatestData = isEqual(debouncedFormData, formData);
    const finishedAllUpdates =
      lastChanged <= lastUpdated &&
      formState.isDirty &&
      formState.isValid &&
      isLatestData;

    if (reset && finishedAllUpdates) {
      reset(debouncedFormData);
    }
  }, [
    reset,
    debouncedFormData,
    formState.isDirty,
    lastUpdated,
    lastChanged,
    formData,
    formState.isValid
  ]);

  const triggerAutosave = useCallback(() => {
    setAutosaveIsTriggered(true);
  }, []);

  const saveImmediately = useCallback(async () => {
    const isFormValid = formState.isValid || allowSavingInvalidForm;

    if (!isFormValid) {
      trigger?.();
      setDataState("error");
      setHasError(true);
      return;
    }

    if (skipUpdateComparator?.(formData)) {
      setDataState("saved");
      setHasError(false);
      setLastUpdated(Date.now());
      return;
    }

    await handleAutoSave(formData, Date.now());
  }, [
    formState.isValid,
    allowSavingInvalidForm,
    trigger,
    skipUpdateComparator,
    formData,
    handleAutoSave
  ]);

  return {
    dataSaveStateContent,
    dataState,
    isUpdating,
    isDebouncing,
    isDirty: formState.isDirty,
    triggerAutosave,
    saveImmediately
  };
}
